import React, { Component } from 'react';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import TpClientLinkItem from '../../../Domain/TpClientLinkItem';
import TpClientLinkService from '../../../Services/tpClientLinkService';
import TpClientLinkSearchParams from './TpClientLinkSearchParams';
import ErrorMessage from '../../../Elements/ErrorMessage';
import FigmaButtonRow from '../../../Elements/FigmaButtonRow';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import TpClientLinkLinkToClientWindowDescription from './TpClientLinkLinkToClientWindowDescription';
import LinkTpClientToClientData from '../../Sales/ClientAdmin/LinkTpClientToClientData';

export default class TpClientLinkAdmin extends Component {
  // Dual mode. Embedded in a client (ClientId prop present): the list is scoped to that client and
  // the link/unlink actions are available. Standalone menu page (no ClientId): every third-party
  // client link is listed, including the ones not yet linked to a Caspian client, and the actions
  // are hidden because they have no parent client to act on.
  //
  // The mode MUST be derived from the absence of ClientId, never from an explicit prop: content.js
  // renders deep-linked/F5 pages as <Comp /> with no props at all, so a prop set in menuitems.js
  // would silently vanish on refresh.
  isStandalone = !this.props.ClientId;

  constructor(props) {
    super(props);
    // Constructed with no argument so the base class keeps its own `isNew` contract (see
    // TpClientLinkSearchParams); the scoping is applied here instead.
    const newSearchItem = new TpClientLinkSearchParams();

    if (props.ClientId) {
      newSearchItem.ClientId = props.ClientId;
    } else {
      // Standalone only: seed the name filter from the url, so the page can be deep-linked
      // pre-filtered (same pattern as ClientInvoiceAdmin). This covers a hand-written
      // /tpclientlink?ThirdPartyClientName=… link; on the /tpclientlink/search?… urls the page
      // pushes itself, SearchParamsBase has already hydrated every field generically.
      const thirdPartyClientName = new URLSearchParams(window.location.search).get('ThirdPartyClientName');
      if (thirdPartyClientName) {
        newSearchItem.ThirdPartyClientName = thirdPartyClientName;
      }
    }

    this.state = {
      searchItem: newSearchItem,
      shownPanel: ShownPanelsEnum.None,
      selectedItem: null,
    };

    this.bindFunctions();
  }

  bindFunctions() {
    this.handleButtonRowButtonClick = this.handleButtonRowButtonClick.bind(this);
    this.refresh = this.refresh.bind(this);
    TpClientLinkItem.clickOnUnlinkApproved = this.clickOnUnlinkApproved.bind(this);
    // Row action menu is static on the item type, so the mode is handed over the same way as the
    // callbacks above.
    TpClientLinkItem.isStandalone = this.isStandalone;
  }

  clickOnUnlinkApproved(thirdPartyClientId) {
    TpClientLinkService.UnlinkApproved(this.props.ClientId, thirdPartyClientId)
      .then((result) => {
        if (result.errorMessage) {
          ErrorMessage.SetErrorMessage(result.errorMessage);
          return;
        }
        this.refresh();
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('TCLA38');
      });
  }

  refresh() {
    this.setState({ shownPanel: ShownPanelsEnum.None, searchItem: { ...this.state.searchItem } });
  }

  handleButtonRowButtonClick(panelId) {
    panelId = panelId === this.state.shownPanel ? ShownPanelsEnum.None : panelId;
    this.setState({ shownPanel: panelId });
  }

  handleClickOnStartSearch(searchItem) {
    // Guard on the prop, not on searchItem.ClientId: in embedded mode the client scoping must be
    // forced onto every search, while standalone must leave it unset so the whole list is searched.
    if (this.props.ClientId) {
      searchItem.ClientId = this.props.ClientId;
    }
    return this.setState({ searchItem });
  }

  renderOnePanel = (panelId, content) => (this.state.shownPanel === panelId ? content : null);

  showPanel(panelId) {
    panelId = panelId === this.state.shownPanel ? ShownPanelsEnum.None : panelId;
    this.setState({ shownPanel: panelId });
  }

  renderButtonsAndPanels() {
    return (
      <>
        {!this.props.IsSubClientsView && !this.isStandalone && (
          <FigmaButtonRow
            className={'InnerMenuButtonFigmaList'}
            buttons={[
              {
                iconName: 'fa-solid fa-plus-circle',
                type: ButtonTypeEnum.NormalIconOnly,
                color: ButtonColorsEnum.Primary,
                iconPosition: IconPositionEnum.Center,
                onClick: () => this.showPanel(ShownPanelsEnum.AddNewItem),
                tooltip: 'Link to Third-Party Client',
                roleKey: 'project_edit',
              },
            ]}
          />
        )}
        {this.renderPanels()}
      </>
    );
  }

  renderAddNewItemPanel() {
    const editedItem = new LinkTpClientToClientData(this.props.ClientId);
    return this.renderOnePanel(
      ShownPanelsEnum.AddNewItem,
      <GeneralEditWizzardBase
        crudType={CrudTypesEnum.Create}
        editedItem={editedItem}
        clickOnCancel={() => this.setState({ shownPanel: ShownPanelsEnum.None })}
        clickOnSave={this.refresh}
        windowDescription={TpClientLinkLinkToClientWindowDescription}
        errorCode={'CA112'}
      />
    );
  }

  renderPanels() {
    // The link panel needs a parent client to link to, so it exists in embedded mode only.
    return <div>{this.isStandalone ? null : this.renderAddNewItemPanel()}</div>;
  }

  render() {
    return (
      <div className='contentwrapper'>
        {this.renderButtonsAndPanels()}
        <TabOfDataTable
          type={TpClientLinkItem}
          serviceFunction={TpClientLinkService.GetTpClientLinkItemList}
          convertDBItemsFunction={TpClientLinkService.ConvertDBItem}
          searchItem={this.state.searchItem}
          searchParamsType={TpClientLinkSearchParams}
          showSearchStrip={true}
          clickOnStartSearch={(searchItem) => this.handleClickOnStartSearch(searchItem)}
          singleSearchLine={true}
          //isExportExcelDisabled={false}
          //clickOnExportExcel={this.handleExportExcelButtonClick}
          singleSearchLinePlaceholder='Search in id, name or source...'
          errorCode='TCLA99'
          afterClickOnCancel={() => this.bindFunctions()}
          // Standalone is a full admin page, so its filters belong in the url and stay shareable.
          // Embedded it must stay off: the grid would push its filters onto the parent client's
          // url and clobber it (same shape as NewProjectAdmin's !this.props.ClientId).
          enableUrlPush={this.isStandalone}
          useClickOnClearAll={true}
        />
      </div>
    );
  }
}
